import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Stepper,
    Step,
    StepLabel,
    Button,
    Fade,
    useTheme
} from '@mui/material';
import {
    UploadFile as UploadFileIcon,
    Category as CategoryIcon,
    EditNote as EditNoteIcon,
    BarChart as BarChartIcon,
    GetApp as GetAppIcon,
} from '@mui/icons-material';

import { useSearchParams } from 'react-router-dom';

import FileUpload from './FileUpload';
import HeaderMapping from './HeaderMapping';
import SDESelection from './SDESelection';
import ExcelPreview from './ExcelPreview';
import useFinancialAPI from './useFinancialAPI';

// Updated steps to include the account head mapping step
const steps = [
    { label: 'Upload Files', icon: <UploadFileIcon /> },
    { label: 'Map Account Heads', icon: <CategoryIcon /> },
    { label: 'Select Add Backs', icon: <EditNoteIcon /> },
    { label: 'Review Excel Report', icon: <BarChartIcon /> },
    { label: 'Download Report', icon: <GetAppIcon /> }
];

const Dashboard = () => {
    const [searchParams] = useSearchParams();
    const companyName = decodeURIComponent(searchParams.get('company-name'));

    const theme = useTheme();
    const [activeStep, setActiveStep] = useState(0);
    const [uploadedFiles, setUploadedFiles] = useState(null);
    const [uploadedParams, setUploadedParams] = useState(null);
    const [uploadedBizType, setUploadedBizType] = useState(null);
    const [uploadedWeightedData, setUploadedWeightedData] = useState(null); // New state for weighted data
    const [selectedSDEs, setSelectedSDEs] = useState([]);
    const [accountHeadMappings, setAccountHeadMappings] = useState(null);
    const [expenseCategorizedHeads, setExpenseCategorizedHeads] = useState([]);

    const {
        loading,
        error,
        expenseHeads,
        excelData,
        accountHeads,
        accountHeadCategories,
        businessType,
        analyzeFiles,
        downloadAnalysisExcel,
        getExpenseHeads,
        getAccountHeads
    } = useFinancialAPI();

    const handleUpload = async (files, fileParams, bizType, downloadExcel = false, weightedData = null) => {
        try {
            setUploadedFiles(files);
            setUploadedParams(fileParams);
            setUploadedBizType(bizType);
            
            // Store weighted average data
            setUploadedWeightedData(weightedData);

            // Get account heads first, pass weighted data
            await getAccountHeads(files, fileParams, bizType, weightedData);

            setActiveStep(1); // Move to account head mapping step
        } catch (err) {
            console.error('Error handling upload:', err);
        }
    };

    const handleAccountHeadMapping = async (mappings) => {
        try {
            if (mappings === null) {
                // User clicked "Back" button
                setActiveStep(0);
                return;
            }

            // Store the full mappings object
            setAccountHeadMappings(mappings);

            // Extract account heads categorized as expenses
            const expenseHeads = Object.keys(mappings).filter(head => {
                const mapping = mappings[head];

                if (typeof mapping === 'object' && mapping.conflictHeader) {
                    return false;
                }

                if (typeof mapping === 'object') {
                    return mapping.category === 'expense' && mapping.category !== 'hide';
                } else {
                    return mapping === 'expense' && mapping !== 'hide';
                }
            });

            setExpenseCategorizedHeads(expenseHeads);

            // Create API-compatible format
            const apiMappingsFormat = {};
            const apiConflictHeaders = {};

            Object.keys(mappings).forEach(head => {
                if (typeof mappings[head] === 'object') {
                    apiMappingsFormat[head] = mappings[head].category;

                    if (mappings[head].conflictHeader) {
                        apiConflictHeaders[head] = mappings[head].conflictHeader;
                    }
                } else {
                    apiMappingsFormat[head] = mappings[head];
                }
            });

            // Pass weighted data along with other parameters
            await getExpenseHeads(
                uploadedFiles,
                uploadedParams,
                uploadedBizType,
                apiMappingsFormat,
                Object.keys(apiConflictHeaders).length > 0 ? apiConflictHeaders : null,
                uploadedWeightedData // Add weighted data parameter
            );

            setActiveStep(2); // Move to SDE selection step
        } catch (err) {
            console.error('Error handling account head mapping:', err);
        }
    };

    const handleSDESelection = (selectedItems) => {
        // Ensure we have unique items
        const uniqueItems = [...new Set(selectedItems)];
        setSelectedSDEs(uniqueItems);
    };

    const handleGenerateExcel = async () => {
        try {
            // Ensure we're passing valid SDEs (only those that are visible)
            const visibleSDEs = selectedSDEs.filter(sde => {
                const sdeLower = typeof sde === 'string' ? sde.toLowerCase() : '';

                // Check for the new mapping format
                if (accountHeadMappings &&
                    accountHeadMappings[sdeLower] &&
                    typeof accountHeadMappings[sdeLower] === 'object') {
                    return accountHeadMappings[sdeLower].category !== 'hide';
                }
                // Old format
                else {
                    return !accountHeadMappings ||
                        !accountHeadMappings[sdeLower] ||
                        accountHeadMappings[sdeLower] !== 'hide';
                }
            });

            // Extract just the category mappings for the API
            const apiMappingsFormat = {};
            if (accountHeadMappings) {
                Object.keys(accountHeadMappings).forEach(head => {
                    if (typeof accountHeadMappings[head] === 'object') {
                        apiMappingsFormat[head] = accountHeadMappings[head].category;
                    } else {
                        apiMappingsFormat[head] = accountHeadMappings[head];
                    }
                });
            }

            // Extract conflict headers
            const conflictHeaders = {};
            if (accountHeadMappings) {
                Object.keys(accountHeadMappings).forEach(head => {
                    if (accountHeadMappings[head] &&
                        typeof accountHeadMappings[head] === 'object' &&
                        accountHeadMappings[head].conflictHeader) {
                        conflictHeaders[head] = accountHeadMappings[head].conflictHeader;
                    }
                });
            }

            // Pass weighted data to the analysis function
            await analyzeFiles(
                uploadedFiles,
                uploadedParams,
                uploadedBizType,
                visibleSDEs,
                apiMappingsFormat,
                Object.keys(conflictHeaders).length > 0 ? conflictHeaders : null,
                uploadedWeightedData // Add weighted data parameter
            );

            setActiveStep(3);
        } catch (err) {
            console.error('Error generating Excel:', err);
        }
    };

    const handleDownload = async () => {
        try {
            await downloadAnalysisExcel(`Valuation - ${companyName}`);
            setActiveStep(4);
        } catch (err) {
            console.error('Error downloading Excel:', err);
        }
    };

    const resetAnalysis = () => {
        setActiveStep(0);
        setUploadedFiles(null);
        setUploadedParams(null);
        setUploadedBizType(null);
        setUploadedWeightedData(null); // Reset weighted data
        setSelectedSDEs([]);
        setAccountHeadMappings(null);
        setExpenseCategorizedHeads([]);

        // Clear localStorage data
        localStorage.removeItem('financial_tool_file_params');
        localStorage.removeItem('financial_tool_biz_type');
        localStorage.removeItem('financial_tool_weighted_average');
        localStorage.removeItem('financial_tool_weights');

        // Clear IndexedDB data
        try {
            const request = indexedDB.open('FinancialToolDB');
            request.onsuccess = (event) => {
                const db = event.target.result;
                const transaction = db.transaction(['files'], 'readwrite');
                const store = transaction.objectStore('files');
                store.clear();
            };
        } catch (error) {
            console.error('Error clearing IndexedDB:', error);
        }

        // Dispatch event to reset FileUpload component state
        window.dispatchEvent(new Event('reset-financial-tool'));
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: theme.palette.background.paper
                }}
            >
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((step, index) => (
                        <Step key={step.label}>
                            <StepLabel
                                StepIconProps={{
                                    icon: step.icon,
                                }}
                            >
                                <Typography variant="body2" fontWeight={500}>
                                    {step.label}
                                </Typography>
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Paper>

            {/* Step 1: File Upload */}
            <Fade in={activeStep === 0} timeout={500}>
                <Box sx={{ display: activeStep === 0 ? 'block' : 'none' }}>
                    <FileUpload
                        onSubmit={handleUpload}
                        loading={loading}
                        error={error}
                    />
                </Box>
            </Fade>

            {/* Step 2: Account Head Mapping */}
            <Fade in={activeStep === 1} timeout={500}>
                <Box sx={{ display: activeStep === 1 ? 'block' : 'none' }}>
                    <HeaderMapping
                        accountHeads={accountHeads}
                        accountHeadCategories={accountHeadCategories}
                        onMappingComplete={handleAccountHeadMapping}
                        loading={loading}
                        error={error}
                    />
                </Box>
            </Fade>

            {/* Step 3: SDE Selection */}
            <Fade in={activeStep === 2} timeout={500}>
                <Box sx={{ display: activeStep === 2 ? 'block' : 'none' }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'primary.light',
                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            mb: 4
                        }}
                    >
                        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                            Select Seller Discretionary Expenses
                        </Typography>
                        <Typography variant="body1" paragraph>
                            Choose which expense items should be treated as Seller Discretionary Expenses (SDEs) and added to the Add Backs section.
                        </Typography>

                        {/* SDE Selection Component */}
                        <SDESelection
                            expenseHeads={expenseCategorizedHeads.length > 0 ? expenseCategorizedHeads : expenseHeads}
                            accountHeadMappings={accountHeadMappings}
                            onSelectionChange={handleSDESelection}
                            loading={loading}
                            useExpenseMappedItems={expenseCategorizedHeads.length > 0}
                        />

                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => setActiveStep(1)}
                            >
                                Back to Account Head Mapping
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleGenerateExcel}
                                disabled={loading}
                            >
                                Generate Excel Report
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            </Fade>

            {/* Step 4: Excel Preview */}
            <Fade in={activeStep === 3} timeout={500}>
                <Box sx={{ display: activeStep === 3 ? 'block' : 'none' }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'primary.light',
                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            mb: 4
                        }}
                    >
                        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                            Excel Report Generated
                        </Typography>
                        <Typography variant="body1" paragraph>
                            Your financial analysis has been processed. You can review the Excel report below.
                        </Typography>

                        {/* Excel Preview Component */}
                        {excelData && (
                            <ExcelPreview
                                excelData={excelData}
                                businessType={businessType}
                            />
                        )}

                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => setActiveStep(2)}
                            >
                                Back to SDE Selection
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleDownload}
                                startIcon={<GetAppIcon />}
                            >
                                Download Excel Report
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            </Fade>

            {/* Step 5: Download Confirmation */}
            <Fade in={activeStep === 4} timeout={500}>
                <Box sx={{ display: activeStep === 4 ? 'block' : 'none' }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            borderRadius: 2,
                            textAlign: 'center',
                            border: '1px solid',
                            borderColor: 'success.light',
                            backgroundColor: 'rgba(46, 125, 50, 0.04)',
                            mb: 4
                        }}
                    >
                        <Typography variant="h5" gutterBottom sx={{ color: 'success.main', fontWeight: 600 }}>
                            Excel Report Downloaded Successfully
                        </Typography>
                        <Typography variant="body1" paragraph>
                            Your financial analysis has been downloaded as an Excel file.
                            The Excel file includes detailed financial metrics, standardized categories, and calculations.
                        </Typography>

                        <Box sx={{ mt: 3 }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={resetAnalysis}
                                sx={{ mr: 2 }}
                            >
                                Start New Analysis
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            </Fade>
        </Container>
    );
};

export default Dashboard;